<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { $lineItemOptions, $multipleUpdateLineItemPriority} from '@/api/brandings'
import { $multipleUpdateSignPriority, $signOptions } from '@/api/signs'
import {$getKopItemOptions, $multipleUpdateKopItemPriority} from '@/api/kop'

const snackbarStore = useSnackbarStore()

interface IProps {
  modelValue?: boolean,
  campaignType?: 'wayfinding' | 'branding' | 'kop',
  selectedItemIds?: number[],
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  campaignType: 'wayfinding',
  selectedItemIds: () => ([]),
})

const emit = defineEmits(['update:modelValue', 'onSubmit', 'onClose'])

interface Priority {
  value: number;
  display_name: string;
}

const selectedPriority = ref<number | null>(null)
const dialogLoading = ref<boolean>(false)
const priorityLoading = ref<boolean>(false)
const priorities = ref<Priority[]>([])

const onSubmit = async () => {
  // KOP
  if (props.campaignType === 'kop') {
    await multiplePriorityUpdateKopItems()
  }
  // Wayfinding
  else if (props.campaignType === 'wayfinding') {
    await multiplePriorityUpdateSigns()
  }
  // Branding
  else {
    await multiplePriorityUpdateLineItems()
  }

  emit('onSubmit')
  closeDialog()
}

const multiplePriorityUpdateKopItems = async () => {
  dialogLoading.value = true
  const payload = {
    priority: selectedPriority.value,
    kop_item_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateKopItemPriority(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item(s) priority(ies) updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const multiplePriorityUpdateSigns = async () => {
  dialogLoading.value = true
  const payload = {
    priority: selectedPriority.value,
    sign_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateSignPriority(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign(s) priority(ies) updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const multiplePriorityUpdateLineItems = async () => {
  dialogLoading.value = true
  const payload = {
    priority: selectedPriority.value,
    line_item_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateLineItemPriority(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) priority(ies) updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const getPriorities = async () => {
  priorityLoading.value = true
  // KOP
  if (props.campaignType === 'kop') {
    await getKopItemOptions()
  }
  // Wayfinding
  else if (props.campaignType === 'wayfinding') {
    await getSignOptions()
  }
  // Branding
  else {
    await getLineItemOptions()
  }
  priorityLoading.value = false
}

const getKopItemOptions = async () => {
  try {
    const res = await $getKopItemOptions()
    priorities.value = res.data.actions.POST.priority.choices
  } catch (e) {
    console.log(e)
  }
}

const getSignOptions = async () => {
  try {
    const res = await $signOptions()
    priorities.value = res.data.actions.POST.priority.choices
  } catch (e) {
    console.log(e)
  }
}

const getLineItemOptions = async () => {
  try {
    const res = await $lineItemOptions()
    priorities.value = res.data.actions.POST.priority.choices
  } catch (e) {
    console.log(e)
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  selectedPriority.value = null
  emit('onClose')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    getPriorities()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <div>
    <VDialog
      :model-value="modelValue"
      max-width="600"
      class="default-dialog"
      :persistent="!!dialogLoading"
      @after-leave="onClose"
      @update:modelValue="emit('update:modelValue')"
    >
      <VBtn
        variant="elevated"
        density="compact"
        icon="tabler-x"
        class="v-dialog-close-btn"
        :disabled="!!dialogLoading"
        @click="closeDialog"
      />

      <VCard title="Update Priority">
        <VCardText>
          <VRow>
            <VCol class="py-0">
              <VAutocomplete
                v-model="selectedPriority"
                :items="[...priorities]"
                :disabled="!!dialogLoading || !!priorityLoading"
                :loading="!!priorityLoading"
                item-title="display_name"
                item-value="value"
                density="compact"
                label="Priority"
                no-data-text="Priorities not found"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol
              class="d-flex justify-end"
              cols="12"
            >
              <VBtn
                :disabled="!!dialogLoading"
                @click="onSubmit"
              >
                <VProgressCircular
                  v-if="dialogLoading"
                  class="mr-1"
                  size="x-small"
                  :width="2"
                  indeterminate
                  color="light"
                />
                Update
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
